import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import {
  surfaceElevationClasses,
  surfaceRadiusClasses,
  surfaceVariantClasses,
} from '../atoms/Surface/Surface';
import { cn } from '../lib/cn';

/**
 * `sunken` sits inside a list or section. `elevated` stands alone in place of
 * a whole panel, so it takes the panel radius. `size` sets the vertical inset.
 */
const emptyVariants = cva(
  'flex w-full min-w-0 flex-col items-center justify-center gap-4 px-4 text-center text-balance',
  {
    variants: {
      variant: {
        sunken: cn(surfaceVariantClasses.sunken, surfaceRadiusClasses.lg),
        elevated: cn(
          surfaceVariantClasses.elevated,
          surfaceElevationClasses.sm,
          surfaceRadiusClasses['2xl'],
        ),
      },
      size: {
        md: 'py-8',
        lg: 'py-12',
      },
    },
    defaultVariants: {
      variant: 'sunken',
      size: 'md',
    },
  },
);

function Empty({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyVariants>) {
  return (
    <div data-slot="empty" className={cn(emptyVariants({ variant, size }), className)} {...props} />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-header"
      className={cn('flex max-w-sm flex-col items-center gap-2', className)}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant }), className)}
      {...props}
    />
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-title"
      className={cn('text-base font-semibold tracking-tight', className)}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-muted-foreground [&>a:hover]:text-primary text-base/normal [&>a]:underline [&>a]:underline-offset-4',
        className,
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex w-full max-w-sm min-w-0 flex-col items-center gap-2.5 text-sm text-balance',
        className,
      )}
      {...props}
    />
  );
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia };
